import Link from "next/link";
import SEOConfig from "@/common/seo.config";
import { SITE } from "@/common/seo.config";
import { Footer, Header, SEO } from "@/components";
import {
  AudienceSplit,
  CtaBand,
  FaqList,
  LandingSection,
  PageHero,
  PrimaryButton,
  SecondaryButton,
  SectionHeader,
} from "@/components/main/landing/landing-ui";
import {
  buildBreadcrumbList,
  buildCollectionPage,
  buildFaqPage,
  buildItemListSchema,
  pageSchemas,
} from "@/lib/schema";

const SIGNALS = [
  {
    title: "Repetitive judgment work",
    description:
      "People apply the same rules to documents or tickets all day, with occasional exceptions that need expertise.",
  },
  {
    title: "Scattered knowledge",
    description:
      "Answers exist in wikis, PDFs, and tribal knowledge, but finding them slows the team.",
  },
  {
    title: "Cross-system friction",
    description:
      "Work stalls because status lives in email, ERP, and spreadsheets without a reliable bridge.",
  },
  {
    title: "Exception backlog",
    description:
      "Happy-path automation exists, but exceptions still pile up for senior operators.",
  },
];

const USE_CASES = [
  {
    title: "Document intake and extraction",
    industry: "Operations · Finance · Logistics",
    description:
      "Classify inbound files, pull structured fields, and route exceptions for human review before systems of record update.",
    approach:
      "OCR or native parsers, validation rules, confidence thresholds, and an exception queue operators already understand.",
    outcomes: "Faster cycle time, fewer manual keystrokes, cleaner data entry",
    href: "/ai-workflow-automation",
  },
  {
    title: "Operational knowledge assistants",
    industry: "Support · HR · Compliance",
    description:
      "Answer questions from policies, SOPs, and internal docs with citations so frontline teams stop hunting through folders.",
    approach:
      "Retrieval over approved sources, citation UX, and escalation when confidence or policy risk is high.",
    outcomes: "Shorter time-to-answer, consistent guidance, fewer escalations",
    href: "/custom-llm-application-development",
  },
  {
    title: "Multi-step operational agents",
    industry: "Ops · Shared services",
    description:
      "Agents that gather context, call tools and APIs, and complete handoffs across systems with explicit review checkpoints.",
    approach:
      "Tool-using agents, human-in-the-loop gates, and audit logs for every material action.",
    outcomes: "Fewer handoffs, higher throughput, audit-friendly trails",
    href: "/ai-agent-development",
  },
  {
    title: "ERP and CRM AI features",
    industry: "Enterprise platforms",
    description:
      "Embed AI into existing platforms: summaries, recommendations, and assisted updates without replacing the core stack.",
    approach:
      "Secure connectors, role-aware prompts, and UI that lives inside the tools people already open daily.",
    outcomes: "Adoption inside familiar tools, lower change-management cost",
    href: "/ai-integrations",
  },
  {
    title: "Invoice and claims processing",
    industry: "Finance · Insurance",
    description:
      "Match documents to purchase orders or policies, flag mismatches, and prepare draft decisions for review.",
    approach:
      "Extraction plus business rules, duplicate detection, and reviewer workflows tied to finance systems.",
    outcomes: "Shorter close cycles, fewer payment errors",
    href: "/ai-workflow-automation",
  },
  {
    title: "Customer and employee chatbots",
    industry: "Support · Internal IT",
    description:
      "Grounded assistants for FAQs, ticket deflection, and guided workflows with clear paths to a human.",
    approach:
      "Knowledge grounding, intent routing, and handoff packages that preserve conversation context.",
    outcomes: "Lower ticket volume, faster first response",
    href: "/solutions/enterprise-ai-chatbot-development",
  },
  {
    title: "Logistics and supply chain support",
    industry: "Logistics · Supply chain",
    description:
      "Shipment context, document processing, and operational Q&A designed for logistics-heavy environments.",
    approach:
      "Connect manifests, trackers, and SOPs so operators get status and next actions in one place.",
    outcomes: "Less exception backlog, clearer status for operators",
    href: "/solutions/ai-solutions-for-logistics-operations",
  },
  {
    title: "Marina and port coordination",
    industry: "Marine · Ports",
    description:
      "Scheduling support, vessel data handling, and operational coordination for marina and port teams.",
    approach:
      "Domain data models, booking and berth workflows, and assistants trained on operational procedures.",
    outcomes: "Smoother berth and service workflows, fewer manual lookups",
    href: "/solutions/ai-solutions-for-marina-management",
  },
  {
    title: "Aviation and maintenance support",
    industry: "Aviation",
    description:
      "Help technicians and planners find procedures, parts context, and maintenance history without digging through manuals.",
    approach:
      "Grounded retrieval over tech docs, controlled answers, and links back to maintenance systems.",
    outcomes: "Faster lookups, fewer tribal-knowledge bottlenecks",
    href: "/custom-llm-application-development",
  },
  {
    title: "Contract and policy Q&A",
    industry: "Legal · Procurement",
    description:
      "Search clauses, obligations, and playbooks with citations so commercial teams move faster with less risk.",
    approach:
      "Document chunking by clause structure, permissioned corpora, and review flags for high-risk answers.",
    outcomes: "Quicker diligence, fewer missed obligations",
    href: "/solutions/ai-knowledge-assistants-for-organizations",
  },
  {
    title: "Sales and ops meeting assistants",
    industry: "Revenue ops",
    description:
      "Turn call notes and emails into structured CRM updates, follow-ups, and summaries for account teams.",
    approach:
      "Speech or text intake, field mapping to CRM, and human confirmation before write-back.",
    outcomes: "Cleaner CRM data, less after-call admin",
    href: "/ai-integrations",
  },
  {
    title: "Quality and compliance checks",
    industry: "Manufacturing · Regulated ops",
    description:
      "Compare submissions or production records against checklists and flag deviations for QA review.",
    approach:
      "Rule and model hybrid checks, evidence capture, and audit-ready exception reports.",
    outcomes: "Earlier defect detection, stronger audit trails",
    href: "/ai-agent-development",
  },
];

const BY_INDUSTRY = [
  {
    title: "Logistics and supply chain",
    items: [
      "Manifest and invoice extraction",
      "Shipment exception triage",
      "Operator status assistants",
    ],
    href: "/solutions/ai-solutions-for-logistics-operations",
  },
  {
    title: "Marine and ports",
    items: [
      "Berth and service scheduling support",
      "Vessel data handling",
      "Procedure Q&A for dock teams",
    ],
    href: "/solutions/ai-solutions-for-marina-management",
  },
  {
    title: "Enterprise operations",
    items: [
      "Knowledge assistants over SOPs",
      "ERP and CRM assisted workflows",
      "Shared-services agents",
    ],
    href: "/solutions",
  },
  {
    title: "Finance and shared services",
    items: [
      "AP document intake",
      "Claims and policy matching",
      "Exception queues with review",
    ],
    href: "/ai-workflow-automation",
  },
];

const EVALUATION = [
  {
    title: "Time and throughput",
    description:
      "Cycle time, items processed per operator hour, and backlog age before and after the pilot.",
  },
  {
    title: "Quality and risk",
    description:
      "Error rates, exception rates, citation accuracy, and how often humans override the system.",
  },
  {
    title: "Adoption",
    description:
      "Whether operators use the system in the real workflow, not only in a demo environment.",
  },
  {
    title: "Operability",
    description:
      "Monitoring, ownership, and whether your team can run the system after handoff.",
  },
];

const INPUTS = [
  "A named owner for the workflow",
  "Sample documents or system exports",
  "Access path for a pilot (even limited)",
  "Success criteria for 60 to 90 days",
];

const FAQS = [
  {
    question: "What if our use case is not listed?",
    answer:
      "Bring the workflow anyway. The catalog is a starting point. Many engagements begin as a custom build once we understand your systems and constraints.",
  },
  {
    question: "How do we pick the first use case?",
    answer:
      "Prefer a painful, measurable workflow with available data and an owner. Avoid starting with the most novel idea if it has the weakest path to production.",
  },
  {
    question: "Can multiple use cases run in one program?",
    answer:
      "Yes, but we usually prove one path first, then expand. Shared foundations (retrieval, connectors, evaluation) make later use cases faster.",
  },
];

export default function UseCasesPage() {
  const structuredData = pageSchemas([
    buildCollectionPage({
      path: "/use-cases",
      name: SEOConfig.useCases.pageTitle,
      description: SEOConfig.useCases.pageDescription,
    }),
    buildItemListSchema({
      name: "Nivalabs AI use cases",
      path: "/use-cases",
      items: USE_CASES.map((item) => ({
        name: item.title,
        path: item.href,
      })),
    }),
    buildFaqPage(FAQS, { id: `${SITE.siteUrl}/use-cases#faq` }),
    buildBreadcrumbList([
      { name: "Home", path: "/" },
      { name: "Use cases", path: "/use-cases" },
    ]),
  ]);

  return (
    <>
      <SEO {...SEOConfig.useCases} structuredData={structuredData} />
      <Header />
      <main>
        <PageHero
          title="Use cases we build for"
          lead="Practical patterns where AI agents, LLM applications, and workflow automation create measurable value for operations teams."
          primaryCta={
            <PrimaryButton href="/contact">Discuss your use case</PrimaryButton>
          }
          secondaryCta={
            <SecondaryButton href="/solutions">View solutions</SecondaryButton>
          }
        />

        <LandingSection>
          <SectionHeader
            title="Signals a use case is ready"
            lead="Strong candidates share operational pain, available data, and an owner who will run the result."
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {SIGNALS.map((item) => (
              <div key={item.title} className="border-t border-line pt-5">
                <h3 className="text-lg font-semibold text-ink">{item.title}</h3>
                <p className="mt-3 text-[0.9875rem] leading-relaxed text-ink-muted">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </LandingSection>

        <LandingSection muted>
          <SectionHeader
            title="Use case catalog"
            lead="Each pattern includes the problem, how we usually approach it, and the service or solution path we recommend."
          />
          <div className="mt-12 space-y-0">
            {USE_CASES.map((item) => (
              <div
                key={item.title}
                className="grid gap-4 border-t border-line py-8 lg:grid-cols-12 lg:gap-8"
              >
                <div className="lg:col-span-4">
                  <p className="text-sm font-medium text-ink-soft">
                    {item.industry}
                  </p>
                  <h3 className="mt-2 text-xl font-semibold text-ink">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm font-medium text-[#1c8c80]">
                    {item.outcomes}
                  </p>
                </div>
                <div className="space-y-3 lg:col-span-6">
                  <p className="text-[0.9875rem] leading-relaxed text-ink-muted">
                    {item.description}
                  </p>
                  <p className="text-[0.9875rem] leading-relaxed text-ink-muted">
                    <span className="font-semibold text-ink">Approach: </span>
                    {item.approach}
                  </p>
                </div>
                <div className="lg:col-span-2 lg:text-right">
                  <Link
                    href={item.href}
                    className="text-sm font-semibold text-ink no-underline hover:text-[#1c8c80]"
                  >
                    Learn more →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </LandingSection>

        <LandingSection>
          <SectionHeader
            title="By industry"
            lead="Domain starting points we adapt to your stack and operating model."
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {BY_INDUSTRY.map((group) => (
              <div key={group.title} className="border-t border-line pt-5">
                <h3 className="text-lg font-semibold text-ink">{group.title}</h3>
                <ul className="mt-4 space-y-2">
                  {group.items.map((item) => (
                    <li
                      key={item}
                      className="flex gap-2 text-sm leading-relaxed text-ink-muted"
                    >
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[#1c8c80]" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link
                  href={group.href}
                  className="mt-4 inline-block text-sm font-semibold text-[#1c8c80] no-underline hover:underline"
                >
                  Explore →
                </Link>
              </div>
            ))}
          </div>
        </LandingSection>

        <LandingSection muted>
          <SectionHeader
            title="How we evaluate success"
            lead="Pilots are judged on operational outcomes, not demo polish."
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {EVALUATION.map((item) => (
              <div key={item.title} className="border-t border-line pt-5">
                <h3 className="text-lg font-semibold text-ink">{item.title}</h3>
                <p className="mt-2 text-[0.9875rem] leading-relaxed text-ink-muted">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </LandingSection>

        <LandingSection>
          <SectionHeader
            title="When to start with a use case vs a custom build"
            lead="Catalog patterns speed discovery. Custom work wins when the workflow is unique."
          />
          <AudienceSplit
            forItems={[
              "Your problem maps to a pattern above",
              "You want a faster path from discovery to pilot",
              "Similar systems and documents already exist in-house",
            ]}
            notForItems={[
              "The workflow is highly custom and poorly documented",
              "Success criteria are still undefined",
              "You only want research with no path to production",
            ]}
          />
        </LandingSection>

        <LandingSection muted>
          <SectionHeader
            title="What we need from you"
            lead="Enough context to scope honestly. You do not need a finished requirements document."
          />
          <ul className="mt-10 max-w-3xl space-y-3">
            {INPUTS.map((item) => (
              <li
                key={item}
                className="flex gap-3 text-[0.9875rem] leading-relaxed text-ink-muted"
              >
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#1c8c80]" />
                {item}
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link href="/how-we-work" className="landing-btn-secondary">
              How we work
            </Link>
            <Link href="/mvp-poc-development" className="landing-btn-secondary">
              MVP and PoC
            </Link>
            <Link href="/faq" className="landing-btn-secondary">
              FAQ
            </Link>
          </div>
        </LandingSection>

        <LandingSection>
          <SectionHeader title="Questions about use cases" />
          <FaqList items={FAQS} />
        </LandingSection>

        <CtaBand
          title="Have a use case in mind?"
          description="Share the workflow and what success would change. We will respond with a clear recommendation."
          secondaryText="Write to us"
          secondaryHref="/contact"
        />
      </main>
      <Footer />
    </>
  );
}
